import { useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { isPreviewHost } from '@/lib/isPreviewHost';
import {
  PREVIEW_CACHE_BUST_KEY,
  buildPreviewCacheBustUrl,
  hasPreviewCacheBustParam,
  shouldForcePreviewRefresh,
  stripPreviewCacheBustParam,
} from '@/lib/previewCacheBypass';

const UPDATE_CHECK_INTERVAL = 60 * 1000; // Check every 60 seconds

export function usePWAUpdate() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [updateSW, setUpdateSW] = useState<((reloadPage?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    if (isPreviewHost(window.location.hostname)) {
      if ('serviceWorker' in navigator) {
        try {
          navigator.serviceWorker.getRegistration?.().then((registration) => {
            if (registration?.active) {
              registration.active.postMessage({ type: 'SKIP_WAITING' });
            }
          }).catch(() => undefined);
        } catch {
          // Ignore messaging failures.
        }

        if (typeof navigator.serviceWorker.getRegistrations === 'function') {
          void navigator.serviceWorker
            .getRegistrations()
            .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
            .catch(() => undefined);
        }
      }

      if (typeof caches !== 'undefined' && typeof caches.keys === 'function') {
        void caches
          .keys()
          .then((cacheNames) => Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName))))
          .catch(() => undefined);
      }

      try {
        const currentSearch = window.location.search;

        if (hasPreviewCacheBustParam(currentSearch)) {
          window.history.replaceState(null, '', stripPreviewCacheBustParam(window.location.href));
        }

        const wasRefreshed = sessionStorage.getItem(PREVIEW_CACHE_BUST_KEY) === '1';
        if (shouldForcePreviewRefresh(currentSearch, wasRefreshed)) {
          sessionStorage.setItem(PREVIEW_CACHE_BUST_KEY, '1');
          window.location.replace(buildPreviewCacheBustUrl(window.location.href));
        }
      } catch {
        // Ignore storage/history failures in constrained webviews.
      }

      return;
    }

    let cleanup: (() => void) | undefined;

    const update = registerSW({
      onNeedRefresh() {
        setNeedRefresh(true);
      },
      onOfflineReady() {
        console.log('App ready to work offline');
      },
      onRegisteredSW(_swUrl, registration) {
        if (!registration) return;

        // Periodic check every 60 seconds
        const intervalId = setInterval(() => {
          if (navigator.onLine) {
            console.log('[PWA] Checking for updates...');
            registration.update();
          }
        }, UPDATE_CHECK_INTERVAL);

        // Check for updates when app becomes visible again
        const handleVisibilityChange = () => {
          if (document.visibilityState === 'visible' && navigator.onLine) {
            console.log('[PWA] App became visible, checking for updates...');
            registration.update();
          }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        cleanup = () => {
          clearInterval(intervalId);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
      },
    });

    setUpdateSW(() => update);

    return () => {
      cleanup?.();
    };
  }, []);

  const applyUpdate = async () => {
    if (updateSW) {
      await updateSW(true); // true = reload page after update
    }
  };

  return { needRefresh, applyUpdate };
}
